import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations } from "../i18n";
import { BADGE_TYPES } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const lload = (key, fb) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fb; } catch { return fb; } };
const lsave = (key, v) => { try { localStorage.setItem(key, JSON.stringify(v)); } catch {} };

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => lload("md_lang", "tr"));
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [wishlist, setWishlist] = useState(() => lload("md_wishlist", []));
  const [compare, setCompare] = useState(() => lload("md_compare", []));

  const [token, setToken] = useState(() => lload("md_token", null));
  const [user, setUser] = useState(() => lload("md_userinfo", null));
  const [adminToken, setAdminToken] = useState(() => lload("md_admin_token", null));

  const t = useCallback((key) => translations[lang][key] || key, [lang]);
  const tr = useCallback((obj) => (obj ? obj[lang] || obj.tr || obj.en || "" : ""), [lang]);

  // ---- fetch helpers ----
  const authHeaders = (isAdmin) => {
    const tk = isAdmin ? adminToken : token;
    return tk ? { Authorization: `Bearer ${tk}` } : {};
  };

  const refreshSettings = useCallback(async () => {
    const r = await fetch(`${API}/settings`);
    if (r.ok) setSettings(await r.json());
  }, []);
  const refreshCategories = useCallback(async () => {
    const r = await fetch(`${API}/categories`);
    if (r.ok) setCategories(await r.json());
  }, []);
  const refreshProducts = useCallback(async () => {
    const r = await fetch(`${API}/products`);
    if (r.ok) setProducts(await r.json());
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([refreshSettings(), refreshCategories(), refreshProducts()]);
      setLoading(false);
    })();
  }, [refreshSettings, refreshCategories, refreshProducts]);

  // visit tracking once per session
  useEffect(() => {
    if (!sessionStorage.getItem("md_visited")) {
      sessionStorage.setItem("md_visited", "1");
      fetch(`${API}/analytics/visit`, { method: "POST" }).catch(() => {});
    }
  }, []);

  // apply brand color
  useEffect(() => {
    const hex = settings?.brandColor || "#800000";
    document.documentElement.style.setProperty("--brand", hex);
    document.documentElement.style.setProperty("--brand-dark", shade(hex, -18));
    document.documentElement.style.setProperty("--brand-soft", hexToRgba(hex, 0.08));
  }, [settings?.brandColor]);

  useEffect(() => {
    const name = settings?.brandName || "MERIDIAN";
    document.title = `${name} — ${lang === "tr" ? "Lüks Saat" : "Fine Watches"}`;
  }, [settings?.brandName, lang]);

  // persist client-side bits
  useEffect(() => lsave("md_lang", lang), [lang]);
  useEffect(() => lsave("md_wishlist", wishlist), [wishlist]);
  useEffect(() => lsave("md_compare", compare), [compare]);
  useEffect(() => lsave("md_token", token), [token]);
  useEffect(() => lsave("md_userinfo", user), [user]);
  useEffect(() => lsave("md_admin_token", adminToken), [adminToken]);

  // ---- wishlist / compare ----
  const toggleWishlist = (id) => setWishlist((w) => (w.includes(id) ? w.filter((x) => x !== id) : [...w, id]));
  const toggleCompare = (id) => setCompare((c) => (c.includes(id) ? c.filter((x) => x !== id) : c.length >= 4 ? c : [...c, id]));
  const clearCompare = () => setCompare([]);

  const getBadges = () => ({ ...BADGE_TYPES, ...((settings && settings.customBadges) || {}) });

  // ---- analytics tracking ----
  const trackProductClick = (id) => { fetch(`${API}/analytics/product-click/${id}`, { method: "POST" }).catch(() => {}); };
  const trackWhatsapp = (id) => { fetch(`${API}/analytics/whatsapp-click/${id}`, { method: "POST" }).catch(() => {}); };
  const fetchAnalytics = async () => {
    const r = await fetch(`${API}/analytics`, { headers: authHeaders(true) });
    if (r.ok) return r.json();
    return null;
  };

  // ---- customer auth ----
  const register = async ({ name, email, password }) => {
    const r = await fetch(`${API}/auth/register`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name, email, password }) });
    const d = await r.json();
    if (!r.ok) return { ok: false, error: d.detail };
    setToken(d.token); setUser(d.user);
    return { ok: true };
  };
  const login = async ({ email, password }) => {
    const r = await fetch(`${API}/auth/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }) });
    const d = await r.json();
    if (!r.ok) return { ok: false, error: d.detail };
    setToken(d.token); setUser(d.user);
    return { ok: true };
  };
  const logout = () => { setToken(null); setUser(null); };

  // ---- admin auth ----
  const adminLogin = async (username, password) => {
    const r = await fetch(`${API}/admin/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, password }) });
    if (!r.ok) return false;
    const d = await r.json();
    setAdminToken(d.token);
    return true;
  };
  const adminLogout = () => setAdminToken(null);
  const changeAdminPassword = async (newPassword) => {
    const r = await fetch(`${API}/admin/change-password`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders(true) }, body: JSON.stringify({ newPassword }) });
    return r.ok;
  };

  // ---- admin CRUD ----
  const jsonAdmin = (method, body) => ({ method, headers: { "Content-Type": "application/json", ...authHeaders(true) }, body: JSON.stringify(body) });

  const saveSettings = async (newSettings) => {
    const r = await fetch(`${API}/settings`, jsonAdmin("PUT", newSettings));
    if (r.ok) { setSettings(await r.json()); return true; }
    return false;
  };
  const createProduct = async (p) => { const r = await fetch(`${API}/products`, jsonAdmin("POST", p)); await refreshProducts(); return r.ok; };
  const updateProduct = async (id, p) => { const r = await fetch(`${API}/products/${id}`, jsonAdmin("PUT", p)); await refreshProducts(); return r.ok; };
  const deleteProduct = async (id) => { const r = await fetch(`${API}/products/${id}`, { method: "DELETE", headers: authHeaders(true) }); await refreshProducts(); return r.ok; };
  const createCategory = async (c) => { const r = await fetch(`${API}/categories`, jsonAdmin("POST", c)); await refreshCategories(); return r.ok; };
  const updateCategory = async (id, c) => { const r = await fetch(`${API}/categories/${id}`, jsonAdmin("PUT", c)); await refreshCategories(); return r.ok; };
  const deleteCategory = async (id) => { const r = await fetch(`${API}/categories/${id}`, { method: "DELETE", headers: authHeaders(true) }); await refreshCategories(); return r.ok; };
  const reorderCategories = async (ids) => { const r = await fetch(`${API}/categories/reorder`, jsonAdmin("POST", { ids })); await refreshCategories(); return r.ok; };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch(`${API}/upload`, { method: "POST", headers: authHeaders(true), body: fd });
    if (!r.ok) return null;
    const d = await r.json();
    return `${BACKEND_URL}${d.url}`;
  };

  const value = {
    lang, setLang, t, tr, loading,
    settings, categories, products,
    refreshProducts, refreshCategories, refreshSettings,
    wishlist, toggleWishlist, compare, toggleCompare, clearCompare, getBadges,
    user, register, login, logout,
    adminAuthed: !!adminToken, adminLogin, adminLogout, changeAdminPassword,
    saveSettings, createProduct, updateProduct, deleteProduct,
    createCategory, updateCategory, deleteCategory, reorderCategories, uploadFile,
    fetchAnalytics, trackProductClick, trackWhatsapp,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

function shade(hex, percent) {
  const num = parseInt(hex.replace("#", ""), 16);
  let r = (num >> 16) + Math.round(2.55 * percent);
  let g = ((num >> 8) & 0x00ff) + Math.round(2.55 * percent);
  let b = (num & 0x0000ff) + Math.round(2.55 * percent);
  r = Math.max(0, Math.min(255, r)); g = Math.max(0, Math.min(255, g)); b = Math.max(0, Math.min(255, b));
  return "#" + (0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1);
}
function hexToRgba(hex, a) {
  const num = parseInt(hex.replace("#", ""), 16);
  return `rgba(${num >> 16},${(num >> 8) & 0x00ff},${num & 0x0000ff},${a})`;
}
